import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { ScheduleRunItemAction, type ProductSchedule } from '@nucleus-commerce/api-client/admin';
import { Alert, AlertDescription } from '@nucleus-commerce/ui/components/alert';
import { Button } from '@nucleus-commerce/ui/components/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@nucleus-commerce/ui/components/select';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@nucleus-commerce/ui/components/sheet';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
} from '@nucleus-commerce/ui/components/table';

import { session } from '@/shared/session/session';

import { useScheduleFormat } from '../hooks/use-schedule-format';
import { useScheduleRun } from '../hooks/use-schedule-run';
import { useScheduleRunItems } from '../hooks/use-schedule-run-items';

import { RunCounts } from './run-counts';
import { RunItemRow } from './run-item-row';
import { RunStatusBadge } from './schedule-status-badge';

const ALL = '__all__';

export interface RunDetailSheetProps {
  schedule: ProductSchedule;
  runId: string | null;
  onClose: () => void;
}

/** One run: when and why it ran, its counts, and every item, filtered by store and action. */
export function RunDetailSheet({ schedule, runId, onClose }: RunDetailSheetProps) {
  const { t } = useTranslation(['schedules', 'common']);
  const format = useScheduleFormat(schedule.timeZone);
  const [storeId, setStoreId] = useState<string | null>(null);
  const [action, setAction] = useState<ScheduleRunItemAction | null>(null);
  const run = useScheduleRun(schedule.id, runId);
  const live = run.data?.status === 'queued' || run.data?.status === 'running';
  const items = useScheduleRunItems(schedule.id, runId, { storeId, action }, live);
  const storeNames = useMemo(
    () => new Map(schedule.stores.map((s) => [s.storeId, s.storeName])),
    [schedule.stores],
  );
  const rows = items.data?.pages.flatMap((page) => page.items) ?? [];
  const myId = session.useSession().me?.user.id;

  return (
    <Sheet
      open={runId !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent side="right" className="w-full gap-0 overflow-y-auto sm:max-w-3xl">
        <SheetHeader className="border-b">
          <SheetTitle>
            {run.data
              ? run.data.cycle === null
                ? t(`schedules:runKind.${run.data.kind}`)
                : t('schedules:runs.titleCycle', {
                    kind: t(`schedules:runKind.${run.data.kind}`),
                    cycle: run.data.cycle,
                  })
              : t('schedules:runs.title')}
          </SheetTitle>
          <SheetDescription>
            {t(`schedules:runKindHint.${run.data?.kind ?? 'rotate'}`)}
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-col gap-4 p-4">
          {run.isError ? (
            <ErrorState error={run.error} onRetry={() => void run.refetch()} compact />
          ) : null}
          {run.data ? (
            <dl className="grid gap-3 text-sm sm:grid-cols-2">
              <div>
                <dt className="text-muted-foreground text-xs">
                  {t('schedules:runs.columns.status')}
                </dt>
                <dd>
                  <RunStatusBadge status={run.data.status} />
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground text-xs">
                  {t('schedules:runs.columns.when')}
                </dt>
                <dd>
                  {run.data.startedAt ? (
                    <span title={format.utc(run.data.startedAt)}>
                      {format.dateTime(run.data.startedAt)}
                    </span>
                  ) : (
                    t('schedules:runs.notStarted')
                  )}
                  {run.data.scheduledFor ? (
                    <span className="text-muted-foreground block text-xs">
                      {t('schedules:runs.dueAt', { when: format.dateTime(run.data.scheduledFor) })}
                    </span>
                  ) : null}
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-muted-foreground text-xs">
                  {t('schedules:runs.columns.counts')}
                </dt>
                <dd>
                  <RunCounts counts={run.data.counts} />
                </dd>
              </div>
              <div className="sm:col-span-2">
                <dt className="text-muted-foreground text-xs">{t('schedules:runs.startedBy')}</dt>
                <dd>
                  {/* Runs carry only the user id; staff names are not readable here, so it says
                      "You" for the signed-in member and "A team member" for anyone else. */}
                  {run.data.createdByUserId === null
                    ? t('schedules:runs.byCadence')
                    : run.data.createdByUserId === myId
                      ? t('schedules:runs.byYou')
                      : t('schedules:runs.byStaff')}
                </dd>
              </div>
            </dl>
          ) : run.isPending && runId ? (
            <Skeleton className="h-16" />
          ) : null}
          {run.data?.error ? (
            <Alert variant="destructive" role="alert">
              <AlertDescription>
                {t('schedules:runs.failedWith', { error: run.data.error })}
              </AlertDescription>
            </Alert>
          ) : null}
          <div className="flex flex-wrap gap-2">
            <Select
              value={storeId ?? ALL}
              onValueChange={(value) => setStoreId(value === ALL ? null : value)}
            >
              <SelectTrigger className="w-52" aria-label={t('schedules:runs.filterStore')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('schedules:runs.allStores')}</SelectItem>
                {schedule.stores.map((store) => (
                  <SelectItem key={store.storeId} value={store.storeId}>
                    {store.storeName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={action ?? ALL}
              onValueChange={(value) =>
                setAction(value === ALL ? null : (value as ScheduleRunItemAction))
              }
            >
              <SelectTrigger className="w-44" aria-label={t('schedules:runs.filterAction')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>{t('schedules:runs.allActions')}</SelectItem>
                {Object.values(ScheduleRunItemAction).map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`schedules:action.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {items.isError ? (
            <ErrorState error={items.error} onRetry={() => void items.refetch()} compact />
          ) : items.isPending && runId ? (
            <div className="flex flex-col gap-2" aria-busy>
              <Skeleton className="h-10" />
              <Skeleton className="h-10" />
            </div>
          ) : rows.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('schedules:runs.noItems')}</p>
          ) : (
            <div className="rounded-md border" data-tour="schedules.detail.run-items">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('schedules:runs.columns.product')}</TableHead>
                    <TableHead>{t('schedules:runs.columns.store')}</TableHead>
                    <TableHead>{t('schedules:runs.columns.action')}</TableHead>
                    <TableHead>{t('schedules:runs.columns.reason')}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((item) => (
                    <RunItemRow
                      key={item.id}
                      item={item}
                      storeName={storeNames.get(item.storeId) ?? t('schedules:runs.removedStore')}
                    />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          {items.hasNextPage ? (
            <Button
              type="button"
              variant="outline"
              className="self-start"
              disabled={items.isFetchingNextPage}
              onClick={() => void items.fetchNextPage()}
            >
              {t('schedules:runs.loadMore')}
            </Button>
          ) : null}
        </div>
      </SheetContent>
    </Sheet>
  );
}
